import React, { useState, useContext, useEffect, useRef } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, btnP, btnSec, sp as spStyle, rad } from "../../constants/styles";
import Fld from "../common/Fld";
import LoadBtn from "../common/LoadBtn";
import Ic from "../common/Ic";
import { safeRemoveMap } from "../../utils/safeRemoveMap";
import useUnsavedWarning from "../../hooks/useUnsavedWarning";

const SIVAKASI = [9.4533, 77.8024];
const TILE_URL = "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";
const TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>';

function MiniMapPicker({ lat, lng, onSelect, h, t }) {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const leafletRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const L = await import("leaflet");
        await import("leaflet/dist/leaflet.css");
        delete L.Icon.Default.prototype._getIconUrl;
        L.Icon.Default.mergeOptions({
          iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
          iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
          shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
        });
        if (!cancelled) { leafletRef.current = L; setReady(true); }
      } catch (e) { console.error("Leaflet load error:", e); }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!ready || !mapContainerRef.current || mapInstanceRef.current) return;
    const L = leafletRef.current;
    const center = lat && lng ? [parseFloat(lat), parseFloat(lng)] : SIVAKASI;
    const zoom = lat && lng ? 16 : 13;
    const map = L.map(mapContainerRef.current, { center, zoom, zoomControl: true });
    L.tileLayer(TILE_URL, { attribution: TILE_ATTR, maxZoom: 19 }).addTo(map);
    if (lat && lng) {
      markerRef.current = L.marker(center, { draggable: true }).addTo(map);
      markerRef.current.on("dragend", () => {
        const pos = markerRef.current.getLatLng();
        onSelect(pos.lat.toFixed(7), pos.lng.toFixed(7));
      });
    }
    map.on("click", (e) => {
      const { lat: cLat, lng: cLng } = e.latlng;
      if (markerRef.current) {
        markerRef.current.setLatLng([cLat, cLng]);
      } else {
        markerRef.current = L.marker([cLat, cLng], { draggable: true }).addTo(map);
        markerRef.current.on("dragend", () => {
          const pos = markerRef.current.getLatLng();
          onSelect(pos.lat.toFixed(7), pos.lng.toFixed(7));
        });
      }
      onSelect(cLat.toFixed(7), cLng.toFixed(7));
    });
    mapInstanceRef.current = map;
    return () => { safeRemoveMap(map); mapInstanceRef.current = null; markerRef.current = null; };
  }, [ready]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mapInstanceRef.current || !leafletRef.current || !lat || !lng) return;
    const pos = [parseFloat(lat), parseFloat(lng)];
    if (markerRef.current) {
      markerRef.current.setLatLng(pos);
    } else {
      markerRef.current = leafletRef.current.marker(pos, { draggable: true }).addTo(mapInstanceRef.current);
      markerRef.current.on("dragend", () => {
        const p = markerRef.current.getLatLng();
        onSelect(p.lat.toFixed(7), p.lng.toFixed(7));
      });
    }
    mapInstanceRef.current.setView(pos, 16);
  }, [lat, lng]); // eslint-disable-line react-hooks/exhaustive-deps

  return <div>
    <div ref={mapContainerRef} style={{ width: "100%", height: 200, borderRadius: rad.md, overflow: "hidden", border: `1px solid ${h.bd}`, marginBottom: spStyle.xs }} />
    <p style={{ color: h.tt, fontSize: 10, margin: 0 }}>{t.tapToPin || "Tap on map to pin location, drag to adjust"}</p>
  </div>;
}

function ServicePersonFormPage({ edit }) {
  const { t, h, sps, setSps, go, flash, selTid } = useContext(Ctx);
  const sp = edit ? sps.find(x => x.id === selTid) : null;
  const [name, setName] = useState(sp?.name || "");
  const [phone, setPhone] = useState(sp?.ph || "");
  const [skill, setSkill] = useState(sp?.skill || "");
  const [active, setActive] = useState(sp?.active !== undefined ? !!sp.active : true);
  // Location — read-only for landlord (SP sets from their own app)
  const spLat = sp?.latitude ? Number(sp.latitude) : null;
  const spLng = sp?.longitude ? Number(sp.longitude) : null;
  const hasLocation = spLat && spLng;
  useUnsavedWarning(name !== (sp?.name || "") || phone !== (sp?.ph || ""));

  const handleSave = async () => {
    if (!name || !phone) { flash(t.namePhoneReq); return; }
    const id = edit ? sp.id : `sp_${Date.now()}`;
    const newSp = {
      id, name, ph: phone,
      password: edit ? (sp.password || "123") : "123", // Keep existing or default
      skill: skill || "General",
      active: active ? 1 : 0,
      latitude: edit ? (sp.latitude || null) : null, // Preserve SP's own location
      longitude: edit ? (sp.longitude || null) : null,
    };
    await api.saveServicePerson(newSp);
    if (edit) { setSps(sps.map(x => x.id === id ? { ...newSp, active: !!newSp.active } : x)); flash(t.spUpdated); }
    else { setSps([...sps, { ...newSp, active: !!newSp.active }]); flash(t.spAdded); }
    go("servicePersons");
  };

  return <div style={{ paddingTop: 16 }}>
    <button onClick={() => go("servicePersons")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>&larr; {t.back}</button>
    <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: "0 0 16px" }}>{edit ? t.edit : t.addServicePerson}</h2>
    <div style={{ ...crd(h), padding: 20 }}>
      <Fld l={t.name} v={name} set={setName} h={h} />
      <Fld l={t.phone} v={phone} set={setPhone} h={h} />
      <Fld l={t.spSkill} v={skill} set={setSkill} h={h} ph="Plumbing, Electrical, General..." />

      {/* Location — read-only for landlord */}
      {edit && <div style={{ marginBottom: spStyle.md }}>
        <label style={{ color: h.ts, fontSize: 12, fontWeight: 600, display: "block", marginBottom: spStyle.sm }}>Shop Location</label>
        {hasLocation ? <div>
          <div style={{ background: `${h.gn}08`, borderRadius: 10, padding: 10, border: `1px solid ${h.gn}20`, marginBottom: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ color: h.gn, fontSize: 12 }}>{"\u2705"}</span>
              <span style={{ color: h.gn, fontSize: 12, fontWeight: 600 }}>Location set by {sp?.name || "SP"}</span>
            </div>
            <div style={{ color: h.sc, fontSize: 11, marginTop: 4 }}>{spLat.toFixed(4)}, {spLng.toFixed(4)}</div>
          </div>
          <MiniMapPicker lat={spLat} lng={spLng} onSelect={() => {}} h={h} t={t} />
        </div> : <div style={{ background: `${h.wn}10`, borderRadius: 10, padding: 12, border: `1px solid ${h.wn}25` }}>
          <div style={{ color: h.wn, fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{"\u26A0\uFE0F"} Location not set</div>
          <div style={{ color: h.sc, fontSize: 11 }}>Service person needs to set their location from their own app (Profile → Use My Location)</div>
        </div>}
      </div>}

      {!edit && <div style={{ background: `${h.ac}08`, borderRadius: 10, padding: 12, border: `1px solid ${h.ac}20`, marginBottom: 12 }}>
        <div style={{ color: h.ac, fontSize: 11, fontWeight: 600 }}>ℹ️ After adding, the service person logs in with Phone + default password "123" and sets their own location & password.</div>
      </div>}

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <label style={{ color: h.sc, fontSize: 13, fontWeight: 500 }}>{t.active}</label>
        <button onClick={() => setActive(!active)} style={{ background: active ? h.gn : h.ib, border: "none", color: active ? "#fff" : h.sc, padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{active ? t.onTxt : t.offTxt}</button>
      </div>
      <LoadBtn onClick={handleSave} label={t.save} style={btnP(h)} />
    </div>
  </div>;
}

export default ServicePersonFormPage;
